import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { downloadFile, generateBlob } from '@chilecompra/react-kit'
import { Toast } from '@chilecompra/react-kit/components'
import { ArrowDownward } from '@mui/icons-material'
import styled from 'styled-components'

import { SNACKBAR_CLOSE_TIMEOUT } from '../../config/settings/constants'
import { getComprobanteEnvioPdf } from '../../services/sellerQuote'

const DEFAULT_FILENAME = 'Comprobante_envio_cotizacion.pdf'

const getFilename = disposition => {
  if (!disposition) return DEFAULT_FILENAME

  const encoded = /filename\*=UTF-8''([^;]+)/i.exec(disposition)
  if (encoded) return decodeURIComponent(encoded[1])

  const quoted = /filename="([^"]+)"/i.exec(disposition)
  if (quoted) return quoted[1]

  const plain = /filename=([^;]+)/i.exec(disposition)
  return plain ? plain[1].trim() : DEFAULT_FILENAME
}

/**
 * Download link for the seller send-receipt PDF, with success/error toasts.
 */
const DownloadComprobanteLink = ({ align, codigoSolicitud, cotizacionId, label }) => {
  const [open, setOpen] = useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return undefined

    const timeoutId = setTimeout(() => setOpen(false), SNACKBAR_CLOSE_TIMEOUT)
    return () => clearTimeout(timeoutId)
  }, [open, error])

  const handleDownload = async event => {
    event.preventDefault()
    if (loading) return

    if (!cotizacionId && !codigoSolicitud) {
      setError(true)
      setOpen(true)
      return
    }

    setLoading(true)
    try {
      const { data, headers } = await getComprobanteEnvioPdf({ cotizacionId, codigoSolicitud })
      const blob = generateBlob(data, headers['content-type'] || 'application/pdf')
      downloadFile(blob, getFilename(headers['content-disposition']))
      setError(false)
      setOpen(true)
    } catch (_error) {
      setError(true)
      setOpen(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <Wrapper $align={align}>
        <Link onClick={handleDownload}>{label}</Link>
        <Arrow />
      </Wrapper>
      {open && (
        <ToastAnchor>
          {error ? (
            <Toast severity="error" title="No ha sido posible realizar la descarga">
              Te pedimos que lo vuelvas a intentar dentro de unos minutos.
            </Toast>
          ) : (
            <Toast severity="success" title="Descarga realizada con éxito." />
          )}
        </ToastAnchor>
      )}
    </>
  )
}

DownloadComprobanteLink.defaultProps = {
  align: 'right',
  codigoSolicitud: undefined,
  cotizacionId: undefined
}

DownloadComprobanteLink.propTypes = {
  align: PropTypes.oneOf(['center', 'right']),
  codigoSolicitud: PropTypes.string,
  cotizacionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  label: PropTypes.string.isRequired
}

const Wrapper = styled('div')`
  align-items: center;
  display: flex;
  justify-content: ${props => (props.$align === 'center' ? 'center' : 'flex-end')};
  margin: 24px 0 0;
  min-height: 24px;
  width: 100%;
`

const Link = styled('a')`
  color: ${props => props.theme.palette?.blue?.[50] || '#0064ff'};
  cursor: pointer;
  font-family: ${props => props.theme.typography.fontFamily};
  font-size: 14px;
  line-height: 18px;
  text-decoration: underline;
`

const Arrow = styled(ArrowDownward)`
  color: ${props => props.theme.palette?.blue?.[50] || '#0064ff'};
  font-size: 18px;
  margin: 0 0 0 4px;
`

const ToastAnchor = styled('div')`
  max-width: 420px;
  position: fixed;
  right: 16px;
  top: 16px;
  z-index: 2000;
`

export default DownloadComprobanteLink
